import { useState } from "react";
import { PhoneIncoming, UserCheck, CalendarSearch, CalendarCheck, Send } from "lucide-react";

const steps = [
  {
    icon: PhoneIncoming,
    number: "01",
    title: "AI Answers Calls & Chats Instantly",
    description: "Your AI receptionist picks up every call on the first ring and responds to chat messages immediately — no hold times, no voicemail.",
    color: "bg-blue-500",
    visual: (
      <div className="space-y-3">
        <div className="flex items-center gap-3 bg-white rounded-lg border border-slate-200 p-3">
          <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center shrink-0">
            <PhoneIncoming className="w-5 h-5 text-green-500" strokeWidth={1.5} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-[#0F172A]">Incoming Call</p>
            <p className="text-xs text-slate-400">+44 7911 234567</p>
          </div>
          <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-0.5 rounded-full">Answered in 0.3s</span>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-3">
          <div className="flex items-start gap-2 mb-2">
            <div className="w-6 h-6 rounded-full bg-[#3B82F6] flex items-center justify-center shrink-0 mt-0.5">
              <span className="text-[10px] font-bold text-white">AI</span>
            </div>
            <p className="text-xs text-slate-600 bg-blue-50 rounded-lg px-3 py-2">"Hello! Thank you for calling Sunrise Dental. How can I help you today?"</p>
          </div>
          <div className="flex items-start gap-2 justify-end">
            <p className="text-xs text-slate-600 bg-slate-50 rounded-lg px-3 py-2">"Hi, I'd like to book a check-up please."</p>
            <div className="w-6 h-6 rounded-full bg-slate-300 flex items-center justify-center shrink-0 mt-0.5">
              <span className="text-[10px] font-bold text-white">SJ</span>
            </div>
          </div>
        </div>
        <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
          <p className="text-xs text-[#3B82F6] font-medium">Average answer time: 0.3 seconds</p>
        </div>
      </div>
    ),
  },
  {
    icon: UserCheck,
    number: "02",
    title: "Captures Customer Details & Request",
    description: "The AI follows your intake flow to collect the caller's name, phone number, and service needed — every time, without fail.",
    color: "bg-indigo-500",
    visual: (
      <div className="space-y-2.5">
        {[
          { label: "Full Name", value: "Sarah Johnson", icon: "check" },
          { label: "Phone Number", value: "+44 7911 234567", icon: "check" },
          { label: "Service Requested", value: "Dental Check-up", icon: "check" },
          { label: "Insurance", value: "AXA — Policy #4421", icon: "check" },
        ].map((field) => (
          <div key={field.label} className="bg-white rounded-lg border border-slate-200 p-3 flex items-center justify-between">
            <div>
              <span className="text-xs text-slate-400">{field.label}</span>
              <p className="text-sm font-medium text-[#0F172A]">{field.value}</p>
            </div>
            <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
              <svg className="w-3 h-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
            </div>
          </div>
        ))}
        <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3">
          <p className="text-xs text-indigo-600 font-medium">All customer details captured — ready for booking</p>
        </div>
      </div>
    ),
  },
  {
    icon: CalendarSearch,
    number: "03",
    title: "Checks Real-Time Calendar Availability",
    description: "Connected to your calendar, the AI instantly sees which slots are open and offers the customer convenient times.",
    color: "bg-amber-500",
    visual: (
      <div className="space-y-2.5">
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <span className="text-xs font-medium text-[#0F172A] mb-3 block">Tuesday, 14 January</span>
          <div className="grid grid-cols-3 gap-2">
            {[
              { time: "9:00 AM", avail: true },
              { time: "10:00 AM", avail: false },
              { time: "11:00 AM", avail: true },
              { time: "1:00 PM", avail: true },
              { time: "2:30 PM", avail: true },
              { time: "4:00 PM", avail: false },
            ].map((slot) => (
              <div key={slot.time} className={`text-center py-2 rounded-lg text-xs ${slot.avail ? "bg-green-50 border border-green-200 text-green-700 font-medium" : "bg-slate-100 text-slate-400 line-through"}`}>
                {slot.time}
              </div>
            ))}
          </div>
        </div>
        <div className="bg-amber-50 border border-amber-100 rounded-lg p-3">
          <p className="text-xs text-amber-700 font-medium">4 available slots found — offering to customer</p>
        </div>
      </div>
    ),
  },
  {
    icon: CalendarCheck,
    number: "04",
    title: "Books Appointments Automatically",
    description: "Once the customer picks a time, the AI confirms the booking instantly and adds it to your calendar — no staff involvement needed.",
    color: "bg-green-500",
    visual: (
      <div className="space-y-3">
        <div className="bg-white rounded-lg border border-green-200 p-4 text-center">
          <div className="inline-flex items-center gap-2 bg-green-50 rounded-full px-4 py-1.5 mb-3">
            <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
            <span className="text-sm font-semibold text-green-700">Appointment Confirmed</span>
          </div>
          <div className="space-y-1 text-left mt-3">
            <p className="text-xs text-slate-500">Client: <span className="text-slate-700 font-medium">Sarah Johnson</span></p>
            <p className="text-xs text-slate-500">Service: <span className="text-slate-700 font-medium">Dental Check-up</span></p>
            <p className="text-xs text-slate-500">Date: <span className="text-slate-700 font-medium">Tue 14 Jan, 1:00 PM</span></p>
            <p className="text-xs text-slate-500">Duration: <span className="text-slate-700 font-medium">30 minutes</span></p>
          </div>
        </div>
        <div className="bg-green-50 border border-green-100 rounded-lg p-3">
          <p className="text-xs text-green-700 font-medium">Added to calendar — confirmation sent to client & practice</p>
        </div>
      </div>
    ),
  },
  {
    icon: Send,
    number: "05",
    title: (<>Sends Conversation Summaries<br className="md:hidden" /> to Staff</>),
    description: "After every interaction, your team receives a clear summary with all details — so they're always in the loop without listening to recordings.",
    color: "bg-[#3B82F6]",
    visual: (
      <div className="space-y-3">
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-[#0F172A]">Call Summary</span>
            <span className="text-xs text-slate-400">2 min ago</span>
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2 py-1">
              <div className="w-1.5 h-1.5 rounded-full bg-[#3B82F6]" />
              <span className="text-xs text-slate-600">New patient Sarah Johnson called for dental check-up</span>
            </div>
            <div className="flex items-center gap-2 py-1">
              <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
              <span className="text-xs text-slate-600">Appointment booked: Tue 14 Jan, 1:00 PM</span>
            </div>
            <div className="flex items-center gap-2 py-1">
              <div className="w-1.5 h-1.5 rounded-full bg-amber-500" />
              <span className="text-xs text-slate-600">Note: Patient asked about insurance coverage (AXA)</span>
            </div>
          </div>
        </div>
        <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
          <p className="text-xs text-[#3B82F6] font-medium">Summary emailed to Dr. Smith & front desk</p>
        </div>
      </div>
    ),
  },
];

export default function HowItWorksSection() {
  const [active, setActive] = useState(0);
  const [expandedMobile, setExpandedMobile] = useState(0);

  return (
    <section id="how-it-works" data-testid="how-it-works-section" className="py-10 md:py-[85px] bg-[#F8FAFC]">
      <div className="section-container">
        <div className="text-center mb-12 md:mb-16">
          <span className="inline-block text-xs font-medium uppercase tracking-widest text-[#3B82F6] mb-4">
            How It Works
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-[#0F172A] tracking-tight text-balance">
            From Call to{" "}
            <span className="text-[#3B82F6]">Booked Appointment</span> in 5 Steps
          </h2>
          <p className="mt-4 text-base md:text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
            A seamless workflow that turns every inbound call or chat into a captured lead<br className="md:hidden" /> and a booked appointment.
          </p>
        </div>

        {/* Desktop: Two-column tab layout */}
        <div className="hidden md:grid md:grid-cols-5 gap-8 lg:gap-12 items-start">
          <div className="col-span-2 space-y-1" data-testid="step-nav-desktop">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = active === index;
              return (
                <button
                  key={step.number}
                  onClick={() => setActive(index)}
                  className={`w-full text-left rounded-xl p-4 transition-all duration-200 group ${
                    isActive
                      ? "bg-white border border-slate-200 shadow-lg shadow-blue-900/5"
                      : "bg-transparent border border-transparent hover:bg-white/60"
                  }`}
                  data-testid={`step-tab-${index}`}
                >
                  <div className="flex items-center gap-3.5">
                    <div
                      className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 transition-colors duration-200 ${
                        isActive ? step.color + " text-white shadow-md" : "bg-slate-100 text-slate-400 group-hover:bg-slate-200"
                      }`}
                    >
                      <Icon className="w-5 h-5" strokeWidth={1.5} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`font-mono text-xs transition-colors duration-200 ${isActive ? "text-[#3B82F6]" : "text-slate-400"}`}>
                          {step.number}
                        </span>
                        <span className={`font-heading font-semibold text-sm transition-colors duration-200 ${isActive ? "text-[#0F172A]" : "text-slate-500"}`}>
                          {step.title}
                        </span>
                      </div>
                      {isActive && (
                        <p className="text-xs text-slate-500 mt-1 leading-relaxed animate-fade-in">
                          {step.description}
                        </p>
                      )}
                    </div>
                  </div>
                  {isActive && (
                    <div className="mt-3 h-0.5 bg-gradient-to-r from-[#3B82F6] to-blue-300 rounded-full" />
                  )}
                </button>
              );
            })}
          </div>

          <div className="col-span-3" data-testid="step-content-desktop">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-xl shadow-slate-200/50 p-6 lg:p-8 min-h-[420px]">
              <div key={active} className="animate-fade-in">
                <div className="flex items-center gap-3 mb-5">
                  <div className={`w-9 h-9 rounded-lg ${steps[active].color} flex items-center justify-center`}>
                    {(() => {
                      const Icon = steps[active].icon;
                      return <Icon className="w-4.5 h-4.5 text-white" strokeWidth={1.5} />;
                    })()}
                  </div>
                  <div>
                    <span className="font-mono text-xs text-[#3B82F6]">Step {steps[active].number}</span>
                    <h3 className="font-heading font-semibold text-lg text-[#0F172A] leading-tight">
                      {steps[active].title}
                    </h3>
                  </div>
                </div>
                <p className="text-sm text-slate-500 leading-relaxed mb-6">
                  {steps[active].description}
                </p>
                <div className="bg-[#F8FAFC] rounded-xl border border-slate-100 p-4 lg:p-5">
                  {steps[active].visual}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile: Accordion */}
        <div className="md:hidden space-y-2" data-testid="step-accordion-mobile">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isOpen = expandedMobile === index;
            return (
              <div
                key={step.number}
                className={`rounded-xl border transition-all duration-200 ${
                  isOpen ? "bg-white border-slate-200 shadow-md" : "bg-white/50 border-slate-100"
                }`}
                data-testid={`step-accordion-${index}`}
              >
                <button
                  onClick={() => setExpandedMobile(isOpen ? -1 : index)}
                  className="w-full text-left p-4 flex items-center gap-3"
                >
                  <div
                    className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 transition-colors duration-200 ${
                      isOpen ? step.color + " text-white" : "bg-slate-100 text-slate-400"
                    }`}
                  >
                    <Icon className="w-4 h-4" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className={`font-mono text-xs ${isOpen ? "text-[#3B82F6]" : "text-slate-400"}`}>{step.number}</span>
                    <h3 className={`font-heading font-semibold text-sm ${isOpen ? "text-[#0F172A]" : "text-slate-500"}`}>
                      {step.title}
                    </h3>
                  </div>
                  <svg
                    className={`w-5 h-5 text-slate-400 transition-transform duration-200 shrink-0 ${isOpen ? "rotate-180" : ""}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {isOpen && (
                  <div className="px-4 pb-4 animate-fade-in">
                    <p className="text-sm text-slate-500 leading-relaxed mb-4">{step.description}</p>
                    <div className="bg-[#F8FAFC] rounded-xl border border-slate-100 p-3">
                      {step.visual}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
